import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import {
  getPrioritySchema,
  resolveCardTitle,
  getFieldLabel,
  type DomainRecord,
  type FieldDef,
} from '@brainpool/profile-schema';

import { formatDomainName } from './parsers';

// ─── Value formatting ─────────────────────────────────────────────────────────

function formatFieldValue(
  value: string | number | boolean | null | undefined,
  type: string
): string {
  if (value === null || value === undefined || value === '') return '';
  if (type === 'checkbox') return value ? 'Yes' : 'No';
  return String(value);
}

// ─── DomainRecordViewer ───────────────────────────────────────────────────────

export function DomainRecordViewer({ domain, record }: { domain: string; record: DomainRecord }) {
  const schema = getPrioritySchema(domain);
  if (!schema) return null;

  const cardTitle = resolveCardTitle(schema, record);
  const fields = schema.fields as Record<string, FieldDef | undefined>;

  // Filter each layout row to only non-empty, non-hidden cells; drop fully-empty rows
  const visibleRows = schema.layout
    .map(row =>
      row.filter(cell => {
        const def = fields[cell.field];
        if (!def || def.hidden) return false;
        const val = record[cell.field];
        return val !== null && val !== undefined && val !== '';
      })
    )
    .filter(row => row.length > 0);

  if (visibleRows.length === 0 && cardTitle) {
    return (
      <Typography
        variant="body2"
        sx={{
          color: 'text.primary',
          fontWeight: 600,
          py: 0.5,
        }}
      >
        {cardTitle}
      </Typography>
    );
  }

  if (visibleRows.length === 0) return null;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: 1,
        position: 'relative',
        p: 2,
        pt: cardTitle ? 2.5 : 2,
        backgroundColor: 'background.paper',
      }}
    >
      {cardTitle && (
        <Typography
          variant="body2"
          sx={{
            position: 'absolute',
            top: -8,
            left: 12,
            px: 0.75,
            backgroundColor: 'background.paper',
            fontWeight: 700,
            color: 'text.primary',
            lineHeight: 1,
          }}
        >
          {cardTitle}
        </Typography>
      )}

      <Stack divider={<Divider />} spacing={0}>
        {visibleRows.map((row, rowIdx) => (
          <Box key={rowIdx} sx={{ display: 'flex', gap: 3, py: 0.75 }}>
            {row.map(cell => {
              const def = fields[cell.field];
              if (!def) return null;
              const displayValue = formatFieldValue(record[cell.field], def.type);
              const label = getFieldLabel(def, cell.field);
              return (
                <Box key={cell.field} sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    {label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.primary',
                      fontWeight: 500,
                      wordBreak: 'break-word',
                      fontSize: '0.8125rem',
                    }}
                  >
                    {displayValue}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

// ─── DomainGroupViewer ────────────────────────────────────────────────────────

export function DomainGroupViewer({
  domain,
  records,
  defaultExpanded = false,
  collapsible = true,
}: {
  domain: string;
  records: DomainRecord[];
  defaultExpanded?: boolean;
  collapsible?: boolean;
}) {
  const schema = getPrioritySchema(domain);

  const hasVisibleLayoutValue = (record: DomainRecord): boolean => {
    if (!schema) return false;
    const f = schema.fields as Record<string, FieldDef | undefined>;
    return schema.layout
      .flatMap(r => r)
      .some(cell => {
        const def = f[cell.field];
        if (!def || def.hidden) return false;
        const val = record[cell.field];
        return val !== null && val !== undefined && val !== '';
      });
  };

  const hasCardTitle = (record: DomainRecord): boolean => {
    if (!schema) return false;
    const cardTitle = resolveCardTitle(schema, record);
    return Boolean(cardTitle && cardTitle.trim() !== '');
  };

  const nonEmptyRecords = records.filter(record => {
    return hasVisibleLayoutValue(record) || hasCardTitle(record);
  });

  if (nonEmptyRecords.length === 0) return null;

  const titleOnlyList =
    nonEmptyRecords.length > 0 &&
    nonEmptyRecords.every(record => !hasVisibleLayoutValue(record) && hasCardTitle(record));

  const recordsContent = (
    <Stack
      spacing={titleOnlyList ? 0 : 2.5}
      divider={titleOnlyList ? <Divider flexItem /> : undefined}
    >
      {nonEmptyRecords.map((record, i) => (
        <DomainRecordViewer key={i} domain={domain} record={record} />
      ))}
    </Stack>
  );

  if (!collapsible) {
    return (
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 1,
          backgroundColor: 'background.paper',
        }}
      >
        <Box sx={{ pt: 2, px: 2, pb: 2 }}>{recordsContent}</Box>
      </Box>
    );
  }

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      sx={{
        mb: 1,
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: '8px !important',
        boxShadow: 'none',
        '&:before': { display: 'none' },
        '&.Mui-expanded': { mb: 1 },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 } }}
      >
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {formatDomainName(domain)}
        </Typography>
        <Chip
          label={nonEmptyRecords.length}
          size="small"
          sx={{
            height: 20,
            fontSize: '0.7rem',
            fontWeight: 700,
            backgroundColor: 'primary.main',
            color: 'white',
          }}
        />
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 1.5, px: 2, pb: 2 }}>{recordsContent}</AccordionDetails>
    </Accordion>
  );
}
